import { useState, useRef, useEffect } from 'react';

/** Per-conversation time limit exceeded */
export const WS_ERROR_CODE_NFGRC006 = 'NFGRC006';

/** Plan recording limit reached */
export const WS_ERROR_CODE_NFGRC005 = 'NFGRC005';

/** Plan subscription expired */
export const WS_ERROR_CODE_NFGRC004 = 'NFGRC004';

export const WS_ERROR_MESSAGES = {
  [WS_ERROR_CODE_NFGRC006]:
    'Your conversation time limit has been reached. We are stopping and saving your recording. Please upgrade your plan for more time.',
  [WS_ERROR_CODE_NFGRC005]:
    'Your plan limit has been reached. We are stopping and saving your recording.',
  [WS_ERROR_CODE_NFGRC004]:
    'Your plan has expired. We are stopping and saving your recording.',
};

export function extractResponseCode(data) {
  return (
    data.code ||
    data.statusCode ||
    data.error_code ||
    data.data?.code ||
    data.data?.statusCode ||
    data.data?.error_code
  );
}

export function isPlanLimitErrorCode(code) {
  return (
    code === WS_ERROR_CODE_NFGRC006 ||
    code === WS_ERROR_CODE_NFGRC005 ||
    code === WS_ERROR_CODE_NFGRC004
  );
}

export async function handlePlanLimitReached(customMessage, refs, callbacks) {
  const { planLimitStopInProgressRef, userInitiatedStopRef, mediaRecorderRef, socketRef } = refs;
  const {
    clearPlanCountdown,
    showPlanWarningToast,
    handleStop,
    clearLiveSessionCookies,
    clearSessionState,
    resetAfterSessionCompleted,
    navigate,
    getConversationId,
    getUserId,
    getTimer,
  } = callbacks;

  if (planLimitStopInProgressRef.current) return;
  planLimitStopInProgressRef.current = true;
  clearPlanCountdown();

  try {
    if (socketRef?.current && socketRef.current.readyState === WebSocket.OPEN) {
      const convId = typeof getConversationId === 'function' ? getConversationId() : null;
      const userId = typeof getUserId === 'function' ? getUserId() : null;
      const time = typeof getTimer === 'function' ? getTimer() : 0;
      if (convId && userId) {
        socketRef.current.send(JSON.stringify({
          action: 'conversation_live_status',
          conversation_id: convId,
          user_id: userId,
          current_status: 'STOPPED',
          time,
        }));
      }
    }
  } catch (sendErr) {
    console.warn('[Record] Failed to send STOPPED action before plan-limit stop:', sendErr);
  }

  userInitiatedStopRef.current = true;
  if (typeof window !== 'undefined') {
    window.isStoppingSession = true;
  }

  try {
    if (
      mediaRecorderRef.current &&
      typeof mediaRecorderRef.current.requestData === 'function' &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.requestData();
    }
  } catch (flushErr) {
    console.warn('[Record] requestData flush failed before plan-limit stop:', flushErr);
  }

  await new Promise((resolve) => setTimeout(resolve, 50));

  showPlanWarningToast(
    customMessage || WS_ERROR_MESSAGES[WS_ERROR_CODE_NFGRC006]
  );

  await new Promise((resolve) => setTimeout(resolve, 100));

  try {
    await handleStop();
  } catch (error) {
    console.error('[Record] Failed to complete recording after plan limit was reached:', error);
    clearLiveSessionCookies();
    clearSessionState();
    if (typeof resetAfterSessionCompleted === 'function') {
      resetAfterSessionCompleted();
    }
    if (typeof window !== 'undefined') {
      window.isStoppingSession = false;
    }
    planLimitStopInProgressRef.current = false;
    userInitiatedStopRef.current = false;
    navigate('/');
  }
}

function startTickingTo(endsAt, refs, setters, handlePlanLimitReached) {
  const { planCountdownIntervalRef, planCountdownTriggeredRef, isPausedRef } = refs;
  const { setPlanCountdown, setPlanCountdownActive } = setters;

  if (planCountdownIntervalRef.current) {
    clearInterval(planCountdownIntervalRef.current);
    planCountdownIntervalRef.current = null;
  }

  const tick = () => {
    if (isPausedRef?.current) return; // freeze countdown while recording is paused
    const remaining = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
    setPlanCountdown(remaining);
    setPlanCountdownActive(true);
    if (remaining <= 0) {
      if (planCountdownIntervalRef.current) {
        clearInterval(planCountdownIntervalRef.current);
        planCountdownIntervalRef.current = null;
      }
      setPlanCountdownActive(false);
      if (!planCountdownTriggeredRef.current) {
        planCountdownTriggeredRef.current = true;
        handlePlanLimitReached(WS_ERROR_MESSAGES[WS_ERROR_CODE_NFGRC005]);
      }
    }
  };

  tick(); 
  planCountdownIntervalRef.current = setInterval(tick, 1000);
}

export function beginPlanCountdownTicker(refs, setters, handlePlanLimitReached) {
  const { planCountdownIntervalRef, planCountdownTriggeredRef, isPausedRef } = refs;
  const { setPlanCountdown, setPlanCountdownActive } = setters;

  if (planCountdownIntervalRef.current) {
    clearInterval(planCountdownIntervalRef.current);
  }
  setPlanCountdownActive(true);
  planCountdownIntervalRef.current = setInterval(() => {
    // Pause the countdown while the recording is paused
    if (isPausedRef.current) return;
    setPlanCountdown((prev) => {
      if (prev === null) return prev;
      const next = prev - 1;
      if (next <= 0) {
        // Stop ticking and trigger the limit-reached flow
        if (planCountdownIntervalRef.current) {
          clearInterval(planCountdownIntervalRef.current);
          planCountdownIntervalRef.current = null;
        }
        setPlanCountdownActive(false);
        if (!planCountdownTriggeredRef.current) {
          planCountdownTriggeredRef.current = true;
          // Same path as NFGRC005 – stops the recording, saves, and navigates home
          handlePlanLimitReached(WS_ERROR_MESSAGES[WS_ERROR_CODE_NFGRC005]);
        }
        return 0;
      }
      return next;
    });
  }, 1000);
}


export function normalizeWarningPayload(data) {
  if (!data || typeof data !== 'object') return null;

  if (!data.warning_message && data.Warning_message) {
    data.warning_message = data.Warning_message;
  }

  const planInfo = data.plan_info || data.data?.plan_info;
  if (planInfo && !planInfo.warning_message && planInfo.Warning_message) {
    planInfo.warning_message = planInfo.Warning_message;
  }

  return planInfo;
}

export function deriveWarningDuration(data, planInfo) {
  let mins = Number(planInfo?.warning_minutes_remaining ?? data.warning_minutes_remaining) || 0;
  let secs = Number(planInfo?.warning_seconds_remaining ?? data.warning_seconds_remaining) || 0;

  if (mins === 0 && secs === 0 && data.expires_at) {
    let expUTC = data.expires_at;
    if (!expUTC.endsWith('Z') && !expUTC.includes('+') && !expUTC.includes('-', 10)) {
      expUTC = expUTC + 'Z';
    }
    const remainingMs = new Date(expUTC).getTime() - Date.now();
    if (remainingMs > 0) {
      const totalSec = Math.floor(remainingMs / 1000);
      mins = Math.floor(totalSec / 60);
      secs = totalSec % 60;
    }
  }

  const warningText = data.warning_message || planInfo?.warning_message;
  if (mins === 0 && secs === 0 && typeof warningText === 'string') {
    const minMatch = warningText.match(/(\d+)\s*minute/i);
    const secMatch = warningText.match(/(\d+)\s*second/i);
    if (minMatch) mins = Number(minMatch[1]) || 0;
    if (secMatch) secs = Number(secMatch[1]) || 0;
  }

  return { mins, secs };
}

export function handleWarningToastAndCountdown(data, planInfo, showPlanWarningToast, schedulePlanCountdownFromPayload) {
  if (planInfo?.warning_message) {
    const timeoutDuration = data.action === 'configuration_speaker' ? 2000 : 10000;
    if (typeof showPlanWarningToast === 'function') {
      showPlanWarningToast(planInfo.warning_message, timeoutDuration);
    }
    if (typeof schedulePlanCountdownFromPayload === 'function') {
      const { mins, secs } = deriveWarningDuration(data, planInfo);
      const payload =
        Number(planInfo.warning_minutes_remaining) > 0 ||
        Number(planInfo.warning_seconds_remaining) > 0
          ? planInfo
          : {
              ...planInfo,
              warning_minutes_remaining: mins,
              warning_seconds_remaining: secs,
            };
      schedulePlanCountdownFromPayload(payload, data.action);
    }
  }

  if (data.warning_message && data.warning_message !== planInfo?.warning_message) {
    if (typeof showPlanWarningToast === 'function') {
      showPlanWarningToast(data.warning_message, 10000);
    }

    const { mins, secs } = deriveWarningDuration(data, planInfo);
    if (typeof schedulePlanCountdownFromPayload === 'function' && (mins > 0 || secs > 0)) {
      schedulePlanCountdownFromPayload(
        {
          warning_minutes_remaining: mins,
          warning_seconds_remaining: secs,
        },
        data.action
      );
    } else if (mins === 0 && secs === 0) {
      console.warn('[Footer Countdown] NOT scheduled — no remaining time derived from payload', data);
    }
  }
}

export function isNearTimeLimitExpiry(timeLimitExpiresAt, thresholdMinutes = 3) {
  if (!timeLimitExpiresAt) return false;
  const minutesUntilExpiry = (new Date(timeLimitExpiresAt).getTime() - Date.now()) / (1000 * 60);
  return minutesUntilExpiry < thresholdMinutes;
}

export function schedulePlanCountdownFromPayload(
  planInfo,
  action,
  refs,
  setters,
  clearPlanCountdown,
  handlePlanLimitReached,
  setPlanCountdownEndsAt
) {
  if (!planInfo) return;
  const mins = Number(planInfo.warning_minutes_remaining) || 0;
  const secs = Number(planInfo.warning_seconds_remaining) || 0;
  const total = mins * 60 + secs;
  if (total <= 0) return;

  const { planCountdownStartTimeoutRef, planCountdownTriggeredRef } = refs;

  // Reset the trigger guard so a NEW warning can fire the stop later
  planCountdownTriggeredRef.current = false;
  // Reset any existing countdown – the latest server value wins
  clearPlanCountdown();

  const toastDuration = action === 'configuration_speaker' ? 2000 : 10000;
  const endsAt = Date.now() + toastDuration + total * 1000;

  setters.setPlanCountdown(total);
  setters.setPlanCountdownActive(true);

  if (typeof setPlanCountdownEndsAt === 'function') {
    setPlanCountdownEndsAt(endsAt);
  }

  planCountdownStartTimeoutRef.current = setTimeout(() => {
    planCountdownStartTimeoutRef.current = null;
    startTickingTo(
      endsAt,
      refs,
      setters,
      handlePlanLimitReached
    );
  }, toastDuration);
}

export function useTimeLimitDisconnect({
  assemblySocketRef,
  conversationId,
  sessionId,
  getCookie,
  getAuthenticatedUserId,
  logger,
  COMPONENT_NAME,
}) {
  const [timeLimitExpiresAt, setTimeLimitExpiresAt] = useState(null);
  const timeLimitDisconnectTimeoutRef = useRef(null);
  const timeLimitDisconnectTriggeredRef = useRef(false);

  useEffect(() => {
    return () => {
      if (timeLimitDisconnectTimeoutRef.current) {
        clearTimeout(timeLimitDisconnectTimeoutRef.current);
      }
    };
  }, []);

  const scheduleTimeLimitDisconnect = (expiresAtStr) => {
    if (!expiresAtStr) return;

    let expiresAtUTC = expiresAtStr;
    if (!expiresAtStr.endsWith('Z') && !expiresAtStr.includes('+') && !expiresAtStr.includes('-', 10)) {
      expiresAtUTC = expiresAtStr + 'Z';
    }

    const expiryDate = new Date(expiresAtUTC);
    if (isNaN(expiryDate.getTime())) {
      logger.error('Invalid time limit expiry timestamp', {
        component: COMPONENT_NAME,
        action: 'INVALID_EXPIRY_TIMESTAMP',
        userId: getAuthenticatedUserId(),
        context: { rawExpiresAt: expiresAtStr }
      });
      return;
    }

    setTimeLimitExpiresAt(expiryDate.toISOString());

    if (timeLimitDisconnectTimeoutRef.current) {
      clearTimeout(timeLimitDisconnectTimeoutRef.current);
    }

    const now = Date.now();
    const expiryMs = expiryDate.getTime();
    const timeUntilExpiry = expiryMs - now;

    if (timeUntilExpiry <= 0) {
      if (assemblySocketRef?.current && assemblySocketRef.current.readyState === WebSocket.OPEN) {
        assemblySocketRef.current.close(1000, 'Time limit reached');
      }
      timeLimitDisconnectTriggeredRef.current = true;
      return;
    }

    timeLimitDisconnectTimeoutRef.current = setTimeout(() => {
      timeLimitDisconnectTimeoutRef.current = null;

      if (!timeLimitDisconnectTriggeredRef.current) {
        timeLimitDisconnectTriggeredRef.current = true;

        logger.info('Time limit reached - disconnecting transcription WebSocket', {
          component: COMPONENT_NAME,
          action: 'TIME_LIMIT_DISCONNECT',
          userId: getAuthenticatedUserId(),
          context: {
            conversationId: conversationId || getCookie('conversation_id'),
            sessionId: sessionId || getCookie('session_id'),
            expiryTime: expiryDate.toISOString()
          }
        });

        if (assemblySocketRef?.current && assemblySocketRef.current.readyState === WebSocket.OPEN) {
          assemblySocketRef.current.close(1000, 'Time limit reached');
        }
      }
    }, timeUntilExpiry);
  };

  return {
    timeLimitExpiresAt,
    scheduleTimeLimitDisconnect,
  };
}

export function usePlanLimitControls({
  isPausedRef,
  mediaRecorderRef,
  userInitiatedStopRef,
  handleStop,
  clearLiveSessionCookies,
  clearSessionState,
  resetAfterSessionCompleted,
  navigate,
  planCountdownEndsAt,
  setPlanCountdownEndsAt,
  socketRef,
  getConversationId,
  getUserId,
  getTimer,
}) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [planWarningToast, setPlanWarningToast] = useState(null);
  const [planCountdown, setPlanCountdown] = useState(null);
  const [planCountdownActive, setPlanCountdownActive] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const planLimitStopInProgressRef = useRef(false);
  const planWarningToastTimeoutRef = useRef(null);
  const planCountdownIntervalRef = useRef(null);
  const planCountdownStartTimeoutRef = useRef(null);
  const planCountdownTriggeredRef = useRef(false); // prevents duplicate stops

  // ── Read endsAt from context, falling back to sessionStorage ──────────────
  const readEndsAt = () => {
    if (typeof planCountdownEndsAt === 'number' && planCountdownEndsAt > Date.now()) {
      return planCountdownEndsAt;
    }
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem('planCountdownEndsAt');
    const ts = raw ? Number(raw) : null;
    return Number.isFinite(ts) && ts > Date.now() ? ts : null;
  };

  const writeEndsAt = (ts) => {
    if (typeof setPlanCountdownEndsAt === 'function') {
      setPlanCountdownEndsAt(ts);
    } else if (typeof window !== 'undefined') {
      if (ts && ts > Date.now()) {
        sessionStorage.setItem('planCountdownEndsAt', String(ts));
      } else {
        sessionStorage.removeItem('planCountdownEndsAt');
      }
    }
  };

  // ── On mount: resume the ticker if a future endsAt is already stored ─────
  useEffect(() => {
    const endsAt = readEndsAt();
    if (endsAt && endsAt > Date.now()) {
      planCountdownTriggeredRef.current = false;
      startTickingTo(
        endsAt,
        { planCountdownIntervalRef, planCountdownTriggeredRef, isPausedRef },
        { setPlanCountdown, setPlanCountdownActive },
        _handlePlanLimitReached
      );
    }

    return () => {
      if (planCountdownIntervalRef.current) {
        clearInterval(planCountdownIntervalRef.current);
        planCountdownIntervalRef.current = null;
      }
      if (planCountdownStartTimeoutRef.current) {
        clearTimeout(planCountdownStartTimeoutRef.current);
        planCountdownStartTimeoutRef.current = null;
      }
    };
  }, []);

  // ── Functions ──────────────────────────────────────────────────────────────

  /** Stop and clear the countdown. Called on stop / when limit is reached. */
  function clearPlanCountdown() {
    if (planCountdownIntervalRef.current) {
      clearInterval(planCountdownIntervalRef.current);
      planCountdownIntervalRef.current = null;
    }
    if (planCountdownStartTimeoutRef.current) {
      clearTimeout(planCountdownStartTimeoutRef.current);
      planCountdownStartTimeoutRef.current = null;
    }
    setPlanCountdown(null);
    setPlanCountdownActive(false);
    writeEndsAt(null);
  }

  /** Show a temporary warning toast; replaces any existing one. */
  function showPlanWarningToast(message, timeoutMs = 10000) {
    if (!message) return;
    if (planWarningToastTimeoutRef.current) {
      clearTimeout(planWarningToastTimeoutRef.current);
      planWarningToastTimeoutRef.current = null;
    }
    setPlanWarningToast(message);
    planWarningToastTimeoutRef.current = setTimeout(() => {
      setPlanWarningToast(null);
      planWarningToastTimeoutRef.current = null;
    }, timeoutMs);
  }

  /** Triggered when any plan-limit error code (NFGRC004/005/006) is received. */
  function _handlePlanLimitReached(customMessage) {
    return handlePlanLimitReached(
      customMessage,
      { planLimitStopInProgressRef, userInitiatedStopRef, mediaRecorderRef, socketRef },
      {
        clearPlanCountdown,
        showPlanWarningToast,
        handleStop,
        clearLiveSessionCookies,
        clearSessionState,
        resetAfterSessionCompleted,
        navigate,
        getConversationId,
        getUserId,
        getTimer,
      }
    );
  }

  /** Schedule the footer countdown from a server warning payload. */
  function _schedulePlanCountdownFromPayload(planInfo, action = null) {
    return schedulePlanCountdownFromPayload(
      planInfo,
      action,
      { planCountdownIntervalRef, planCountdownStartTimeoutRef, planCountdownTriggeredRef, isPausedRef },
      { setPlanCountdown, setPlanCountdownActive },
      clearPlanCountdown,
      _handlePlanLimitReached,
      writeEndsAt
    );
  }

  return {
    // State
    planWarningToast,
    planCountdown,
    planCountdownActive,

    // Refs
    planLimitStopInProgressRef,
    planCountdownTriggeredRef,

    // Functions
    clearPlanCountdown,
    showPlanWarningToast,
    clearPlanWarningToast: () => setPlanWarningToast(null),
    handlePlanLimitReached: _handlePlanLimitReached,
    schedulePlanCountdownFromPayload: _schedulePlanCountdownFromPayload,
  };
}